import { useState, useEffect, useCallback } from 'react';

/**
 * Hook para controle de tema (light/dark/auto).
 *
 * - 'auto': segue preferência do SO (default)
 * - 'light': forçado claro
 * - 'dark': forçado escuro
 *
 * Persiste no localStorage('cmax_theme').
 * Seta data-theme no <html> para que o CSS aplique as variáveis corretas.
 */
const STORAGE_KEY = 'cmax_theme';

function getSystemPreference() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme) {
  const resolved = theme === 'auto' ? getSystemPreference() : theme;
  if (resolved === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.setAttribute('data-theme', 'light');
  }
}

export default function useTheme() {
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'auto';
  });

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    if (newTheme === 'auto') {
      localStorage.removeItem(STORAGE_KEY);
      // Remove data-theme para deixar o @media funcionar
      document.documentElement.removeAttribute('data-theme');
    } else {
      localStorage.setItem(STORAGE_KEY, newTheme);
      applyTheme(newTheme);
    }
  }, []);

  // Cicla entre: auto → light → dark → auto
  const toggleTheme = useCallback(() => {
    const next = theme === 'auto' ? 'light' : theme === 'light' ? 'dark' : 'auto';
    setTheme(next);
  }, [theme, setTheme]);

  // Na montagem, aplica o tema salvo
  useEffect(() => {
    if (theme !== 'auto') {
      applyTheme(theme);
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [theme]);

  // Escuta mudança na preferência do SO (para modo auto)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (theme === 'auto') {
        // Em modo auto, não setamos data-theme — CSS @media cuida
        document.documentElement.removeAttribute('data-theme');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Resolve qual é o tema visual atual
  const resolved = theme === 'auto' ? getSystemPreference() : theme;

  return { theme, resolved, setTheme, toggleTheme };
}
